/**
 * 核心领域模型（数据模型真源）。
 *
 * 对应规格 §24 的全部实体，并吸收决策 14.x 的修正：
 *   - 字段统一使用 `rir` / `target_rir`（修正 1），不使用 `ri` / `target_ri`；
 *   - 重量口径使用 `weightUnitScope`（决策 14.15），数据层保存原始口径、不私自换算；
 *   - 新增 DailyIntent（决策 14.10，不用 ReadinessCheck 代替）；
 *   - Exercise 移除静态 `enabledForCurrentInjury`，改为派生字段（决策 14.3），
 *     新增 `conditionalRules` 承载条件性动作规则。
 *
 * 说明：
 *   - 这些类型是前端（Dexie 仓储 + Supabase 客户端）与规则引擎共享的语义层。
 *   - 数据库端（Postgres）以迁移脚本落地对应表；字段名在 DB 中为 snake_case，
 *     本文件使用 camelCase 作为语义层，映射关系由迁移/SDK 层处理。
 *   - 所有表主键使用客户端生成的 UUID（服务端同值），用于幂等去重与跨设备合并。
 */

import type {
  ActivityLevel,
  BalanceDemand,
  BalanceDemandAllowed,
  CompoundType,
  ConditionalExerciseRule,
  Confidence,
  EquipmentType,
  FatigueLevel,
  FootStatus,
  GoalType,
  IncrementType,
  DailyIntentType,
  JointPainState,
  LoadLevel,
  MealProcessingStatus,
  MealType,
  MeasurementContext,
  MedicalClearanceStage,
  MovementPattern,
  MountingDifficulty,
  MuscleSoreness,
  OverallDifficulty,
  OverallRecovery,
  Posture,
  ProgressionDecisionType,
  ProgressionReasonCode,
  RestrictionLevel,
  RirValue,
  Sex,
  SleepQuality,
  StabilityLevel,
  SyncOperation,
  SyncStatus,
  WeightBearingLevel,
  WeightUnitScope,
  WorkoutExerciseStatus,
  WorkoutPlanStatus,
  WorkoutPlanType,
  WorkoutSessionStatus,
  WorkoutSetStatus,
} from "./enums";

// ---------------------------------------------------------------------------
// Profile & injury
// ---------------------------------------------------------------------------

export interface UserProfile {
  id: string;
  name: string;
  sex: Sex;
  birthDate: string; // ISO date
  heightCm: number;
  timezone: string; // IANA
  preferredUnits: "metric" | "imperial";
  activityLevel: ActivityLevel;
  goalType: GoalType;
  targetBodyFatPercent?: number | null;
  targetDate?: string | null;
  createdAt: string; // ISO datetime
  updatedAt: string; // ISO datetime
}

export interface InjuryConstraint {
  id: string;
  userId: string;
  bodyPart: string; // 例如 "right_foot_little_toe"
  side: "left" | "right" | "both" | "n/a";
  injuryType: string; // 例如 "fracture"
  injuryDate: string; // ISO date

  // 决策 14.2 —— 各维度独立取值（不合并成单一风险等级）
  weightBearingLevel: WeightBearingLevel;
  forefootRestriction: RestrictionLevel;
  heelRestriction: RestrictionLevel;
  balanceDemandAllowed: BalanceDemandAllowed;
  ankleMovementAllowed: boolean; // 允许勾脚/绷脚
  medicalClearanceStage: MedicalClearanceStage;

  notes?: string | null;
  active: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Exercise（运动动作，结构化，版本化）
// ---------------------------------------------------------------------------

export type Muscles = string[];

export interface Exercise {
  id: string;
  nameZh: string;
  nameEn: string;
  primaryMuscles: Muscles;
  secondaryMuscles: Muscles;
  movementPattern: MovementPattern;
  equipmentType: EquipmentType;
  posture: Posture;
  compoundType: CompoundType;
  stabilityLevel: StabilityLevel;
  forefootLoad: LoadLevel;
  heelLoad: LoadLevel;
  balanceDemand: BalanceDemand;
  mountingDifficulty: MountingDifficulty;
  requiresWeightCarrying: boolean;
  requiresPartner: boolean;
  failureAllowedLastSet: boolean;
  defaultSets: number;
  repMin: number;
  repMax: number;
  targetRir: RirValue;
  restSeconds: number;
  incrementType: IncrementType;
  defaultIncrementKg?: number | null;

  safetyInstructions: string[];
  contraindicationTags: string[];

  /** 条件性动作规则（决策 14.3）：不能用自然语言安全说明代替结构化判定。 */
  conditionalRules: ConditionalExerciseRule[];
  alternativeIds: string[];
  /** 医疗许可前硬锁：true 时无论其他标签如何都默认不可用，且不能靠普通编辑/替换解锁。 */
  lockedUntilMedicalClearance: boolean;

  version: number;

  // curation metadata
  createdAt: string;
  updatedAt: string;
  reviewStatus: "draft" | "approved" | "deprecated";
  source: "seed" | "user" | "curation";
}

/**
 * 派生字段（决策 14.3）：由规则引擎动态计算，非持久化真源。
 * 示例输出到生成结果，不作为动作库长期保存字段。
 */
export interface DerivedExerciseAvailability {
  exerciseId: string;
  exerciseVersion: number;
  isEnabledForCurrentInjury: boolean;
  unmetConditions: ConditionalExerciseRule["id"][];
  reasonCodes: string[];
}

// ---------------------------------------------------------------------------
// Week / daily intent
// ---------------------------------------------------------------------------

export interface PlanSlot {
  id: string;
  /** 槽位描述（来自 §7 建议结构，UI 也可展示） */
  description: string;
  /** 首选动作 id（按顺序取第一个可用且未使用的） */
  preferredIds: string[];
  /** 回退搜索：期望主肌群（动作 primaryMuscles 包含即匹配） */
  muscle?: string | undefined;
  /** 回退搜索：期望动作模式 */
  pattern?: MovementPattern | undefined;
  /** 回退搜索：只接受孤立动作（E 日/孤立槽位） */
  isolationOnly?: boolean | undefined;
  /** 轮换：这些动作若在「上一次正式训练」已用过，则排到首选末尾 */
  rotateRecent?: string[] | undefined;
  /** 该槽位回退搜索失败时是否允许「无匹配即删除」（默认 true） */
  dropWhenEmpty?: boolean | undefined;
}

export interface TrainingTemplate {
  id: string;
  userId: string;
  code: string;
  name: string;
  slots: PlanSlot[];
  position: number;
  isOptionalLightDay: boolean;
  source: "default" | "custom";
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface TrainingSequence {
  id: string;
  userId: string;
  code: "A" | "B" | "C" | "D" | "E";
  name: string;
  position: number;
  isOptional: boolean;
  active: boolean;
}

/**
 * 决策 14.10 —— 每日首次打开的独立意图实体（不用 ReadinessCheck 代替）。
 *
 * 唯一性约束：`(userId, localDate)` 唯一，用于「跨设备不重复询问」。
 * 不得把「已完成训练」后意图改成休息而使训练消失。
 */
export interface DailyIntent {
  id: string;
  userId: string;
  localDate: string; // 用户本地自然日 YYYY-MM-DD
  timezone: string; // 记录时的时区
  intent: DailyIntentType;
  selectedAt: string;
  deviceId: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Workout plan / session / exercise / set
// ---------------------------------------------------------------------------

export interface WorkoutPlan {
  id: string;
  userId: string;
  sequenceCode: "A" | "B" | "C" | "D" | "E";
  plannedDate: string; // 本地自然日
  planType: WorkoutPlanType; // 与 status 分开（决策 14.7）
  readinessAdjustment: ReadinessAdjustmentSnapshot | null; // 生成时的准备度调整摘要
  estimatedMinutes: number;
  status: WorkoutPlanStatus;
  generationRuleVersion: string;
  createdAt: string;
  updatedAt: string;
}

export interface ReadinessAdjustmentSnapshot {
  planType: WorkoutPlanType;
  allowProgression: boolean;
  volumeMultiplier: number;
  loadMultiplier: number;
  requireExerciseRefilter: boolean;
  allowFailure: boolean;
  reasonCodes: string[];
}

export interface WorkoutSession {
  id: string;
  userId: string;
  planId?: string | null;
  deviceId: string;
  startedAt: string; // ISO
  finishedAt?: string | null;
  status: WorkoutSessionStatus;
  planType?: WorkoutPlanType;
  readinessSnapshot?: ReadinessAdjustmentSnapshot | null;
  overallDifficulty?: OverallDifficulty | null;
  footStatusAfter?: FootStatus | null;
  jointIssueAfter?: JointPainState | null;
  notes?: string | null;
  createdOffline: boolean;
  syncStatus: SyncStatus;

  // 决策 14.6 —— 训练归属日
  timezoneAtStart: string; // IANA
  localDateAtStart: string; // YYYY-MM-DD（归属日/今日是否完成的判据）

  createdAt: string;
  updatedAt: string;
}

export interface WorkoutExercise {
  id: string;
  sessionId: string;
  exerciseId: string;
  exerciseVersion: number;
  orderIndex: number;
  suggestedWeightKg: number | null;
  actualBaseWeightKg: number | null;
  weightUnitScope: WeightUnitScope; // 决策 14.15：保存原始口径
  targetSets: number;
  repMin: number;
  repMax: number;
  targetRir: RirValue;
  restSeconds: number;
  status: WorkoutExerciseStatus;
  replacementForId?: string | null;
  replacementReason?: string | null;
  skipReason?: string | null;
  progressionDecisionId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WorkoutSet {
  id: string;
  workoutExerciseId: string;
  setIndex: number;
  suggestedWeightKg: number | null;
  actualWeightKg: number | null;
  weightUnitScope: WeightUnitScope; // 每条组记录自描述口径（决策 14.15；e1RM/容量只同口径比较）
  reps: number | null;
  rir: RirValue | null; // RIR：4=很轻松，2=合适，1=很吃力，0=力竭（决策 14.8 允许如实记录）
  reachedFailure: boolean;
  muscleDiscomfort: boolean;
  jointPain: boolean;
  injuryPain: boolean;
  formBreakdown: boolean;
  restStartedAt?: string | null;
  restTargetEndAt?: string | null;
  actualRestSeconds?: number | null;
  completedAt?: string | null;
  status: WorkoutSetStatus;
  /** 决策 14.8：禁止力竭动作记录 RIR 0 时标记（不阻断保存，仅重新判定进阶）。 */
  unplannedFailure: boolean;
  version: number; // 冲突时保留多版本（复合主键 [id, version]）
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Readiness / recovery / progression
// ---------------------------------------------------------------------------

export interface ReadinessCheck {
  id: string;
  userId: string;
  date: string; // 本地自然日
  sleepQuality: SleepQuality;
  fatigueLevel: FatigueLevel;
  muscleSoreness: MuscleSoreness;
  footStatus: FootStatus;
  jointPain: JointPainState;
  jointPainLocations?: string[] | null;
  resultingAdjustment: ReadinessAdjustmentSnapshot;
  createdAt: string;
}

export interface RecoveryCheck {
  id: string;
  userId: string;
  sourceSessionId: string;
  date: string;
  muscleSoreness: MuscleSoreness;
  jointPain: JointPainState;
  footStatusComparedToBaseline: FootStatus;
  overallRecovery: OverallRecovery;
  createdAt: string;
}

export interface ProgressionDecision {
  id: string;
  userId: string;
  exerciseId: string;
  sourceSessionId: string;
  decision: ProgressionDecisionType;
  previousWeightKg: number | null;
  suggestedWeightKg: number | null;
  weightUnitScope: WeightUnitScope;
  reasonCodes: ProgressionReasonCode[];
  humanReadableReason: string;
  ruleVersion: string;
  userAccepted: boolean;
  userOverrideWeightKg?: number | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Nutrition
// ---------------------------------------------------------------------------

export interface MealEntry {
  id: string;
  userId: string;
  mealType: MealType;
  consumedAt: string; // ISO（决策 14.6：饮食按实际 consumedAt 日期归属）
  rawText: string;
  processingStatus: MealProcessingStatus;
  aiEstimateId?: string | null;
  finalCalories: number | null;
  finalProtein: number | null;
  finalFat: number | null;
  finalCarbs: number | null;
  userCorrected: boolean;
  deviceId: string;
  syncStatus: SyncStatus;
  createdAt: string;
  updatedAt: string;
}

export interface ParsedFoodItem {
  name: string;
  portionText: string;
  caloriesMid: number;
  proteinMid: number;
  fatMid: number;
  carbsMid: number;
}

/** 决策 14.9：供应商可配置，但统一 NutritionEstimate Schema（版本化）。 */
export interface NutritionEstimate {
  caloriesLow: number;
  caloriesHigh: number;
  caloriesMid: number;
  proteinLow: number;
  proteinHigh: number;
  proteinMid: number;
  fatLow: number;
  fatHigh: number;
  fatMid: number;
  carbsLow: number;
  carbsHigh: number;
  carbsMid: number;
  confidence: Confidence;
  assumptions: string[];
  uncertaintySources: string[];
  parsedItems: ParsedFoodItem[];
}

export interface AIEstimate {
  id: string;
  userId: string;
  mealEntryId: string;
  provider: string;
  model: string;
  schemaVersion: string;
  // 三个分量（low/high/mid）各 4 项
  caloriesLow: number;
  caloriesHigh: number;
  caloriesMid: number;
  proteinLow: number;
  proteinHigh: number;
  proteinMid: number;
  fatLow: number;
  fatHigh: number;
  fatMid: number;
  carbsLow: number;
  carbsHigh: number;
  carbsMid: number;
  confidence: Confidence;
  assumptionsJson: string;
  uncertaintyJson: string;
  parsedItemsJson: string;
  promptTokens: number | null;
  completionTokens: number | null;
  estimatedCostCny: number;
  createdAt: string;
}

export interface NutritionTarget {
  id: string;
  userId: string;
  effectiveFrom: string; // ISO date
  calories: number;
  proteinGrams: number;
  fatGrams: number;
  carbsGrams: number;
  reasonCodes: string[];
  calibrationWindowDays: number;
  userAccepted: boolean;
  createdAt: string;
}

/** 决策 14.4 初始目标（首版常量，可由用户修改）。 */
export const INITIAL_NUTRITION_TARGET = {
  calories: 2200,
  caloriesLow: 2100,
  caloriesHigh: 2300,
  proteinGrams: 170,
  fatGrams: 70,
  carbsGrams: 220,
} as const;

// ---------------------------------------------------------------------------
// Measurement / trends
// ---------------------------------------------------------------------------

export interface BodyMeasurement {
  id: string;
  userId: string;
  date: string; // 本地自然日
  weightKg: number;
  waistCm?: number | null;
  bodyFatPercent?: number | null;
  measurementContext: MeasurementContext;
  deviceId: string;
  syncStatus: SyncStatus;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Notification / device / sync / AI usage
// ---------------------------------------------------------------------------

export interface NotificationSetting {
  id: string;
  userId: string;
  enabled: boolean;
  fixedTime: string; // HH:mm（决策 21：具体时间作为设置项，不硬编码）
  daysOfWeek: number[]; // 0=周日 ... 6=周六
  primaryDeviceId?: string | null;
  restTimerNotifications: boolean;
  trainingReminderNotifications: boolean;
  timezone: string;
  updatedAt: string;
}

export interface Device {
  id: string;
  userId: string;
  deviceName: string;
  platform: "ios" | "android" | "web" | "other";
  pushSubscription?: string | null;
  isPrimaryNotificationDevice: boolean;
  lastSeenAt: string;
  createdAt: string;
}

export interface SyncEvent {
  id: string; // 客户端生成 UUID（幂等键来源）
  userId: string;
  deviceId: string;
  entityType: string;
  entityId: string;
  operation: SyncOperation;
  entityVersion: number;
  payload: unknown;
  clientCreatedAt: string;
  serverReceivedAt?: string | null;
  syncStatus: SyncStatus;
}

export interface AIUsage {
  id: string;
  userId: string;
  date: string; // 自然日
  requestCount: number;
  promptTokens: number;
  completionTokens: number;
  estimatedCostCny: number;
  budgetCny: number;
  updatedAt: string;
}
